import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ICustomMeasure } from '@se/models';
import { MediaService } from '../../lib/media.service';
import { EcgConfiguration } from '../../app/resolvers/ecg-configuration';
import { EcgChartComponent } from '../ecg-chart/ecg-chart.component';
import { LayoutFabToolsComponent, SchillerFilters } from '../ecg-fab-tools/layout-fab-tools/layout-fab-tools.component';
import { ICustomMeasureButton } from '../measures/caliper-measures/caliper-measures.component';
import { EcgState } from '../view-models/ecgstate';
import { EcgMeasuresService } from '../utils/ecg-measures.service';
import { ChartViewModel } from '../view-models/chart-viewmodel';
import { MatDialog } from '@angular/material/dialog';
import { CustomDialogComponent } from '../custom-dialog/custom-dialog.component';
import { Subscription } from 'rxjs';
import { createSelector, Store } from '@ngrx/store';
import { IArrangementConfiguration } from '../models/interfaces';
import { ArrangementConfiguration } from '../view-models/arrangement-configuration';
import { SeSpinnerComponent } from '../se-spinner/se-spinner.component';
import { SeFullScreenComponent } from '../se-fullscreen/se-fullscreen.component';
import { EcgSummaryComponent } from '../ecg-summary/ecg-summary.component';
import { CaliperAndMeasuresComponent } from '../measures/caliper-and-measures/caliper-and-measures.component';
import { NgIf } from '@angular/common';
import { LeadsAnnotationsComponent } from '../measures/annotations/leads-annotations/leads-annotations.component';
import { FixedFiltersComponent } from '../fixed-filters/fixed-filters.component';
import { TranslatePipe } from '@ngx-translate/core';

// Selectors for the buttons toolbar feature in the state management (avoid import se-platform)
export const selectSeButtonsToolbarFeature = (state) => state?.buttonsToolbar;

const selectSeButtonsToolbarRightSlider = createSelector(selectSeButtonsToolbarFeature, (state) => state?.rightSlider);

@Component({
    selector: 'ecg-viewer',
    templateUrl: './ecg-viewer.component.html',
    styleUrls: ['./ecg-viewer.component.scss'],
    standalone: true,
    imports: [
        SeSpinnerComponent,
        SeFullScreenComponent,
        EcgSummaryComponent,
        CaliperAndMeasuresComponent,
        NgIf,
        EcgChartComponent,
        LeadsAnnotationsComponent,
        LayoutFabToolsComponent,
        FixedFiltersComponent,
        TranslatePipe,
    ],
})
export class EcgViewerComponent implements OnInit, OnChanges {
    caliperActivated = new EventEmitter<boolean>();
    @ViewChild(EcgChartComponent) ecgChart?: EcgChartComponent;

    @Input() chart!: ChartViewModel;
    @Input() customMeasureButtons: ICustomMeasureButton[] = new Array<ICustomMeasureButton>();
    @Input() customMeasures!: Array<ICustomMeasure>;
    @Input() ecgState = new EcgState();
    @Input() enableMeasureOptions = true;
    @Input() ecgConfiguration!: EcgConfiguration;
    @Input() readOnly = false;
    @Input() enableMeasuresOnlyAtFullscreen = false;
    @Input() hideWhileCentering: boolean = true;
    @Input() reportingWithoutTrace = false;
    @Input() sopInstanceUID!: string;
    @Input() schillerFilters!: SchillerFilters;
    @Input() printAsDisplayed!: boolean;
    @Input() chartTitle!: string;
    @Input() showGlobalMeasures: boolean = false;
    @Input() isEcgReportingDetail: boolean = false; // non mostrare sotto menu fab-tool per misure globali/singolo canale. il mat button rounded va di default su misure per singolo canale se presenti vedi bug-101740
    @Input() isApprovedOrSigned: boolean | undefined;

    @Output() ecgConfigurationChange = new EventEmitter<EcgConfiguration>();
    @Output() lowFilterChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() customMeasuresChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    changeFullWidth: boolean = false;
    isFabVisible = false;
    fullscreen = false;
    rightSliderSubscription?: Subscription;
    showCustomMeasures = false;
    showGlobalAnnotations = false;
    showLeadsAnnotations = false;

    constructor(
        private dialog: MatDialog,
        private ecgMeasuresService: EcgMeasuresService,
        private store: Store,
        public mediaService: MediaService,
    ) {}

    get showCaliperAndMeasures(): boolean {
        if (!this.enableMeasuresOnlyAtFullscreen) return this.canShowCustomMeasures() || this.canShowGlobalMeasures();

        return this.fullscreen && (this.canShowCustomMeasures() || this.canShowGlobalMeasures());
    }

    get showCustomMeasuresButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        return this.customMeasures && this.customMeasures.length > 0;
    }

    get showGlobalAnnotationsButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        return this.chart.activeLayer.ecgData.originalWaveform.annotations.length > 0;
    }

    get showLeadsAnnotationsButton(): boolean {
        if (!this.chart || !this.chart.activeLayer || !this.chart.activeLayer.ecgData) return false;

        if (
            this.chart.activeLayer.ecgData.derivedWaveform.channels == null ||
            this.chart.activeLayer.ecgData.derivedWaveform.channels.length == 0
        )
            return false;

        return (
            this.chart.activeLayer.ecgData.derivedWaveform.channels
                .map((channel) => (channel.annotations ? channel.annotations.length : 0))
                .reduce((total, amount) => total + amount) > 0
        );
    }

    private _arrangementConfiguration: IArrangementConfiguration | null = null;

    get arrangementConfiguration(): IArrangementConfiguration | null {
        this.updateArrangementConfiguration();
        return this._arrangementConfiguration;
    }

    ngOnInit() {
        this.rightSliderSubscription = this.store.select(selectSeButtonsToolbarRightSlider).subscribe((el) => {
            this.changeFullWidth = !!el?.enabled;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.customMeasures?.currentValue || changes.isApprovedOrSigned?.currentValue) {
            if (this.isApprovedOrSigned == true) return;
            else if (!!this.customMeasures && this.isApprovedOrSigned == false)
                this.ecgMeasuresService.updateMeasures(this.customMeasures);

            this.showLeadsAnnotations = false;
        }
        if (changes.schillerFilters?.currentValue) {
            this.checkOldFilter(changes.schillerFilters.currentValue);
        }
    }

    // public functions

    onCloseCustomMeasures() {
        this.onCaliperActivate(false);
    }

    onCaliperActivate(active: boolean) {
        if (active) this.onCustomMeasuresChange(active);
        this.showGlobalAnnotations = active;
    }

    onFullscreenChange() {
        this.fullscreen = !this.fullscreen;
        this.ecgChart?.fit();
    }

    onLowFilterChange(event: number) {
        this.lowFilterChange.emit(event);
    }

    onCustomMeasuresChange(active: boolean) {
        if (this.showCustomMeasures === active) return;
        this.showCustomMeasures = active;
        this.ecgChart?.fit();
        this.customMeasuresChange.emit(active);
    }

    onGlobalAnnotationsChange() {
        this.showGlobalAnnotations = !this.showGlobalAnnotations;
        this.ecgChart?.fit();
    }

    onLeadsAnnotationsChange(active: boolean) {
        this.showLeadsAnnotations = active;
        this.ecgChart?.fit();
    }

    canShowGlobalMeasures(): boolean {
        return (
            !!this.showGlobalAnnotations &&
            !!this.chart &&
            !!this.chart.activeLayer &&
            !!this.chart.activeLayer.ecgData &&
            !!this.chart.activeLayer.ecgData.originalWaveform.annotations &&
            this.chart.activeLayer.ecgData.originalWaveform.annotations.length > 0
        );
    }

    canShowCustomMeasures(): boolean {
        return this.showCustomMeasures && this.customMeasures && this.customMeasures.length > 0;
    }

    canShowCaliperMeasures(): boolean {
        return this.showCustomMeasures && !!this.ecgChart && this.ecgChart.measuresSummary.visible;
    }

    // private functions

    private checkOldFilter(filters: SimpleChange) {
        let cValue = filters.currentValue;

        if (cValue && cValue.lowFilter !== undefined && cValue.highFilter !== undefined) {
            if (cValue.lowFilter === -1 || cValue.highFilter === -1) {
                // ECG & Visita + ECG
                this.dialog.open(CustomDialogComponent, {
                    width: '25%',
                    height: '30%',
                    data: {
                        description: 'ECG_OldFiltersWarning',
                        title: 'Warning',
                        buttonCancel: false,
                        buttonOk: true,
                    },
                });
            }
        }
    }

    private updateArrangementConfiguration() {
        if (!this.chart || !this.chart.activeLayer || this.chart.activeLayer.traces.length === 0) {
            this._arrangementConfiguration = null;
        } else {
            if (!this._arrangementConfiguration) {
                this._arrangementConfiguration = new ArrangementConfiguration();
            }

            this._arrangementConfiguration.leadCount = this.chart.activeLayer.leadCount;
            this._arrangementConfiguration.derivedWaveformPresent =
                !!this.chart.activeLayer.traces?.[0]?.channelSelector?.derivedChannel;
        }
    }
}
